/**
 * #1091 (F3) — contratos especiais vistos pelo EC.
 *
 * Os tipos espelham a ALLOWLIST de `backend/app/services/special_contract_ec.py`: nada interno
 * da revenda (observação, autor, custo do CP, margem) chega aqui. Dinheiro vem como string com
 * 2 casas ("110.00") — a tarifa unitária de excedente, com até 4 ("0.0040"); quantidade
 * (limite, consumo) como número — limite zero é `0`.
 */
import type { MemoriaDoVoucher } from '../utils/voucher'
import { apiClient } from './api'
import type { ResumoDoVoucher } from './specialContractsApi'

export type StatusDoContrato = 'active' | 'cancelled' | 'expired'
export type ModalidadeDoContratoDoCliente = 'resource_block' | 'simple_discount' | 'voucher'

export type ContratoDoCliente = {
  id: string
  name: string
  contract_type: ModalidadeDoContratoDoCliente
  status: StatusDoContrato
  reseller_name: string | null
  start_date: string
  end_date: string | null
  cancelled_at: string | null
  currency: string
  escopo: 'all' | 'product_type' | 'product' | null
  escopo_tipo: string | null
  escopo_produto_nome: string | null
}

/** Termos de uma revisão: os do bloco OU o do desconto, conforme a modalidade. */
export type TermosDoContrato = {
  block_vcpu_limit?: number | null
  block_memory_gb_limit?: number | null
  block_storage_gb_limit?: number | null
  block_fixed_price?: string | null
  overage_price_per_vcpu?: string | null
  overage_price_per_memory_gb?: string | null
  overage_price_per_storage_gb?: string | null
  discount_percentage?: string | null
  // voucher (#1107)
  voucher_credit_amount?: string | null
  voucher_price?: string | null
  voucher_rollover?: 'accumulate' | 'reset' | null
  voucher_release_policy?: 'after_payment' | 'at_start_with_grace' | null
  voucher_grace_days?: number | null
  voucher_late_payment_carry?: boolean | null
}

export type RevisaoDoContrato = {
  numero: number
  vigente_desde: string
  termos: TermosDoContrato
}

export type VinculoDoContrato = {
  resource_type: string
  resource_id: string
  resource_name: string | null
  vinculado_em: string
  /** só no desconto: tabela → percentual → final */
  preco_tabela: string | null
  percentual: string | null
  preco_final: string | null
}

/**
 * Memória de cálculo de um lançamento (allowlist: dias, preços, revisões; no voucher, as chaves
 * de `MemoriaDoVoucher`, que a régua `linhasDaMemoriaDoVoucher` põe em frases).
 */
export type MemoriaDeCalculo = Omit<MemoriaDoVoucher, 'tipo'> & {
  tipo?: string
  dias_do_mes?: number
  /** excedente: dias com custo */
  dias?: number
  total?: string | number
  revisoes?: Array<{
    numero?: number
    dias?: number
    preco?: string | number
    total?: string | number
  }>
}

export type LancamentoDoContrato = {
  competence_period: string | null
  reference_type: string | null
  amount: string
  created_at: string
  calculo: MemoriaDeCalculo | null
  invoice_id: string | null
}

export type DetalheDoContrato = ContratoDoCliente & {
  vale_a_partir_de: string
  termos: TermosDoContrato
  revisoes: RevisaoDoContrato[]
  vinculos: VinculoDoContrato[]
  /** só no bloco */
  consumo: { vcpu: number; memoria_gb: number; disco_gb: number; vms: number } | null
  excedente_do_mes: { competencia: string; valor: string } | null
  lancamentos: LancamentoDoContrato[]
  /** só no voucher (#1107): concessão, consumo, saldo, virada e cobranças */
  voucher?: ResumoDoVoucher
}

export type ListaDeContratosDoCliente = {
  contracts: ContratoDoCliente[]
  total: number
}

export const specialContractsCustomerApi = {
  /** Meus contratos especiais — todos os status por padrão. */
  list: (status?: StatusDoContrato): Promise<ListaDeContratosDoCliente> =>
    apiClient.get('/api/v1/end-customer/special-contracts', {
      params: status ? { status } : {},
    }),

  /** Detalhe de um contrato meu (404 para o que não é meu, com o texto de inexistente). */
  get: (id: string): Promise<DetalheDoContrato> =>
    apiClient.get(`/api/v1/end-customer/special-contracts/${id}`),
}

export default specialContractsCustomerApi
